import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import MessageDialog from "../../components/MessageDialog";
import StorageService from "../../services/StorageService";
import ProjectService from "../../services/ProjectService";
import StoreService from "../../services/StoreService";
import StockReturnReportService from "../../services/stockReturnReportService";
import utility from "../../utils/utility";
import XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from '../../assets/images/deviops_logo.png';
import { addPdfLogo, getPdfLogoDataUrl } from "../../utils/pdfLogo";
import ProjectStoreService from "../../services/ProjectStoreService";
import LocationService from "../../services/LocationService";
import ProjectLocationService from "../../services/ProjectLocationService";
import ProjectCategoryService from "../../services/ProjectCategoryService";

export default function StockReturnReport() {
  const activeCompany = StorageService.activeCompany();
  const companyId = activeCompany?.companyId || "";
  const loginData = StorageService.loginData();
  const employeeId = loginData?.employeeId || "";
  const roleId = loginData?.roleId || "";

  const [projectCategoryId, setProjectCategoryId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [storeId, setStoreId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("");

  const [projectCategories, setProjectCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stores, setStores] = useState([]);
  const [locations, setLocations] = useState([]);

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");

  // ✅ PAGINATION STATES
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const selectedCategory = projectCategories.find(
    (cat) => String(cat.id) === String(projectCategoryId)
  );
  const selectedCategoryCode = selectedCategory?.categoryCode;

  const filteredProjects = projectCategoryId
    ? projects.filter((project) => {
      if (project.projectCategoryId) {
        return (
          String(project.projectCategoryId) ===
          String(projectCategoryId)
        );
      }
      return (
        project.projectCategoryCode ===
        selectedCategoryCode
      );
    })
    : [];



  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  const formatProjectDisplay = (project) => {
    const safeProject = typeof project === "string"
      ? { projectName: project }
      : (project || {});

    if (!safeProject) return "-";

    const projectName = safeProject.projectName || safeProject.name || "";
    const categoryName = safeProject.projectCategoryName || safeProject.categoryName || safeProject.category || "";
    const projectCode = safeProject.projectCode || "";
    const categoryInitial = String(categoryName).trim().charAt(0).toUpperCase();

    if (projectName) {
      if (categoryInitial) {
        return `${categoryInitial}*${projectName}`;
      }

      if (projectCode) {
        const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
        return `${codeInitial}*${projectName}`;
      }

      return projectName;
    }

    if (projectCode) {
      const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
      return `${codeInitial}*`;
    }

    return categoryInitial ? `${categoryInitial}*` : "-";
  };

  const loadDropdowns = async () => {
    try {
      const user = StorageService.loginData();
      let projectRes;

      if (Number(user?.roleId) === 1 || Number(user?.roleId) === 2) {
        projectRes = await ProjectService.getActive(companyId);
      } else {
        projectRes = await ProjectService.getActiveProjectsByEmployee(
          companyId,
          user?.employeeId
        );
      }

      const [projectCategoryRes, storeRes, locationRes] = await Promise.all([
        ProjectCategoryService.getAll(),
        StoreService.getActive(),
        LocationService.getActive(),
      ]);

      setProjectCategories(projectCategoryRes?.data || projectCategoryRes || []);

      let projectList = [];
      if (Array.isArray(projectRes)) {
        projectList = projectRes;
      } else if (Array.isArray(projectRes?.data)) {
        projectList = projectRes.data;
      } else if (Array.isArray(projectRes?.data?.data)) {
        projectList = projectRes.data.data;
      }

      setProjects(projectList);
      setStores(storeRes?.data || storeRes || []);
      setLocations(locationRes?.data || locationRes || []);
    } catch (error) {
      console.error(error);
      setProjectCategories([]);
      setProjects([]);
      setStores([]);
      setLocations([]);
      showMessage("Failed to load filter dropdowns");
    }
  };

  const loadStoresByProject = async (projectId) => {
    if (!projectId) {
      setStores([]);
      return;
    }

    try {
      const res = await ProjectStoreService.getProjectStoreById(projectId);
      setStores(res?.data || res || []);
    } catch (err) {
      console.error("Store load error", err);
      setStores([]);
    }
  };

  const loadLocationsByProjectAndStore = async (projectId, storeId) => {
    if (!projectId || !storeId) {
      setLocations([]);
      return;
    }

    try {
      const res = await ProjectLocationService.getByProjectAndStore(projectId, storeId);
      setLocations(res?.data || []);
    } catch (err) {
      console.error("Location load error", err);
      setLocations([]);
    }
  };

  const loadReport = async (pageNumber = page, overrides = {}) => {
    const requestStartDate = overrides.startDate ?? startDate;
    const requestEndDate = overrides.endDate ?? endDate;

    if (projectCategoryId || projectId) {
      if (!projectCategoryId) {
        showMessage("Project Category is required");
        return;
      }

      if (!projectId) {
        showMessage("Project Name is required");
        return;
      }
    }

    if ((projectCategoryId || projectId) && (!requestStartDate || !requestEndDate)) {
      showMessage("From Date and To Date are required");
      return;
    }

    if (requestStartDate && requestEndDate && new Date(requestStartDate) > new Date(requestEndDate)) {
      showMessage(
        "From Date should be less than or equal to To Date"
      );
      return;
    }

    setLoading(true);
    try {
      const res = await StockReturnReportService.getReport({
        companyId,
        employeeId,
        roleId,
        projectCategoryId: projectCategoryId || undefined,
        projectId: projectId || undefined,
        storeId: storeId || undefined,
        locationId: locationId || undefined,
        status: status || undefined,
        startDate: requestStartDate || undefined,
        endDate: requestEndDate || undefined,
        page: pageNumber,
        limit,
      });

      console.log("Stock Return Report Data:", res?.data);

      // ✅ IMPORTANT
      setReports(res?.data || []);
      setTotalPages(Number(res?.pagination?.totalPages) || 0);
      setTotalRecords(Number(res?.pagination?.total) || 0);
      setPage(pageNumber);

    } catch (error) {
      console.error(error);
      setReports([]);
      showMessage("Error fetching stock return report");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (companyId) {
      loadDropdowns();

      const today = new Date();
      setEndDate(today.toISOString().split("T")[0]);
      loadReport(1);
    }
  }, [companyId]);

  // Reset pagination to first page when any filter changes
  useEffect(() => {
    setPage(1);
  }, [projectCategoryId, projectId, storeId, locationId, status, startDate, endDate]);

  const handleClearFilters = () => {
    setProjectCategoryId("");
    setProjectId("");
    setStoreId("");
    setLocationId("");
    setStatus("");
    setStartDate("");

    const today = new Date();
    setEndDate(today.toISOString().split("T")[0]);

    // ✅ RESET PAGINATION
    setPage(1);
    setTotalPages(0);
    setTotalRecords(0);

    loadReport(1);
  };

  const getAllReportData = async () => {
    try {
      let allData = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const res = await StockReturnReportService.getReport({
          companyId: companyId || undefined,
          employeeId,
          roleId,
          projectCategoryId: projectCategoryId || undefined,
          projectId: projectId || undefined,
          storeId: storeId || undefined,
          locationId: locationId || undefined,
          status: status || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          page: currentPage,
          limit: 10,
        });

        const data = res?.data || [];
        const pagination = res?.pagination || {};

        allData = [...allData, ...data];

        totalPages = pagination.totalPages || 1;
        currentPage++;

      } while (currentPage <= totalPages);

      return allData;

    } catch (error) {
      console.error("PDF error:", error);
      return [];
    }
  };

  const exportExcel = async () => {
    const allData = await getAllReportData();

    if (!allData.length) {
      showMessage("No data to export");
      return;
    }
    const toNumber = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const sortedData = [...allData].sort((a, b) => {
      const dateA = new Date(a.returnDate || 0).getTime();
      const dateB = new Date(b.returnDate || 0).getTime();
      return dateA - dateB;
    });

    const wb = XLSX.utils.book_new();
    const ws = {};
    ws["!merges"] = [];

    // Get filter labels
    let projectCategoryLabel = "All";
    if (projectCategoryId) {
      const found = projectCategories.find(pc => String(pc.id) === String(projectCategoryId));
      projectCategoryLabel = found?.categoryName || projectCategoryId;
    }

    let projectLabel =
      allData[0]?.projectDisplay || "All";


    let storeLabel = "All";
    if (storeId) {
      const found = stores.find(s => String(s.id) === String(storeId));
      storeLabel = found?.storeName || storeId;
    }



    let row = 1;

    // STYLES
    const thin = { style: "thin" };
    const center = {
      horizontal: "center",
      vertical: "center",
      wrapText: true
    };

    // COMPANY NAME (Top Left)
    const companyName = activeCompany?.companyName || "";
    XLSX.utils.sheet_add_aoa(ws, [[companyName]], { origin: { r: 0, c: 0 } });

    // REPORT DATE (Top Right)
    XLSX.utils.sheet_add_aoa(
      ws,
      [[`Report Date: ${utility.formatExportDateTime(new Date())}`]],
      { origin: { r: 0, c: 8 } } // adjust column for right alignment
    );

    // STYLE COMPANY NAME
    ws[XLSX.utils.encode_cell({ r: 0, c: 0 })].s = {
      font: { bold: true, sz: 12 }
    };

    // STYLE REPORT DATE
    ws[XLSX.utils.encode_cell({ r: 0, c: 8 })].s = {
      alignment: { horizontal: "right" },
      font: { bold: true, sz: 11 }
    };
    // TITLE
    XLSX.utils.sheet_add_aoa(ws, [["Stock return Report"]], { origin: { r: row, c: 0 } });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 10 } });
    const titleCell = XLSX.utils.encode_cell({ r: row, c: 0 });
    ws[titleCell].s = {
      font: { bold: true, sz: 16 },
      alignment: center,
      border: {
        top: thin,
        bottom: thin,
        left: thin,
        right: thin
      }
    };
    row += 2;

    // FILTER INFO
    XLSX.utils.sheet_add_aoa(ws, [

      [
        "Project:",
        projectLabel,
        "",
        "From Date:",
        startDate
          ? utility.formatDateDDMMMYYYY(startDate)
          : "-"
      ],
      [
        "",
        "",
        "",
        "To Date:",
        endDate
          ? utility.formatDateDDMMMYYYY(endDate)
          : "-"
      ]
    ], { origin: { r: row, c: 0 } });

    const filterLabelStyle = {
      font: { bold: true, sz: 10 },
      alignment: { horizontal: "left", vertical: "center" },
      border: { top: thin, bottom: thin, left: thin, right: thin }
    };

    const filterValueStyle = {
      font: { sz: 10 },
      alignment: { horizontal: "left", vertical: "center" },
      border: { top: thin, bottom: thin, left: thin, right: thin }
    };

    const boldLabelColumns = [0, 3];
    for (let i = 0; i < 3; i++) {
      for (let c = 0; c < 5; c++) {
        const cell = XLSX.utils.encode_cell({ r: row + i, c });
        if (!ws[cell]) continue;
        ws[cell].s = boldLabelColumns.includes(c) ? filterLabelStyle : filterValueStyle;
      }
    }
    row += 5;

    // TABLE HEADER
    const headers = [
      "Stock Return Date",
      "Store Name",
      "From Location",
      "Received By",
      "Transporter",
      "Project",
      "Voucher Number",
      "Item Category",
      "Item Code",
      "Item Description",
      "UOM",
      "Qty",
      "Status"
    ];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: { r: row, c: 0 } });

    headers.forEach((_, colIndex) => {
      const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
      ws[cell].s = {
        font: { bold: true, sz: 11 },
        alignment: center,
        border: {
          top: thin,
          bottom: thin,
          left: thin,
          right: thin
        }
      };
    });
    row++;

    // TABLE DATA
    sortedData.forEach((reportRow) => {
      const rowData = [
        reportRow.returnDate || "-",
        reportRow.storeName || "-",
        reportRow.locationName || "-",
        reportRow.receivedBy || "-",
        reportRow.transporterName || "-",
        formatProjectDisplay(reportRow),
        reportRow.vocherNo || "-",
        reportRow.itemCategoryName || "-",
        reportRow.itemCode || "-",
        reportRow.itemDescription || "-",
        reportRow.UOM || "-",
        Number(reportRow.quantity || 0),
        reportRow.status || "-"
      ];

      XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });

      rowData.forEach((_, colIndex) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
        ws[cell].s = {
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: thin,
            bottom: thin,
            left: thin,
            right: thin
          }
        };

        // Format date column (index 0 for returnDate)
        if (colIndex === 0 && ws[cell].v) {
          const excelSerial = utility.convertDateToExcelSerial(ws[cell].v);
          if (excelSerial) {
            ws[cell].v = excelSerial;
            ws[cell].t = 'n';
            ws[cell].z = 'dd-mmm-yyyy';
          }
        }

        if (colIndex === 11) {
          ws[cell].t = 'n';
          ws[cell].z = '0.000';
        }
      });
      row++;
    });

    // Set column widths
    ws["!cols"] = [
      { wch: 15 },
      { wch: 12 },
      { wch: 15 },
      { wch: 15 },
      { wch: 18 },
      { wch: 20 },
      { wch: 18 },
      { wch: 30 },
      { wch: 40 },
      { wch: 24 },
      { wch: 20 },
      { wch: 8 },
      { wch: 10 },
      { wch: 12 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, "StockReturnReport");
    XLSX.writeFile(wb, "StockReturnReport.xlsx");
  };

  const exportPdf = async () => {
    const allData = await getAllReportData();

    if (!allData.length) {
      showMessage("No data to export");
      return;
    }

    let projectLabel = "All";
    if (projectId) {
      const found = projects.find(p => String(p.id) === String(projectId));
      projectLabel = formatProjectDisplay(found || { projectName: projectId });
    }

    const doc = new jsPDF("landscape");
    const pdfLogo = await getPdfLogoDataUrl(logo);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageMargin = 14;
    const right = pageWidth - pageMargin;
    const left = pageMargin;
    const companyName = activeCompany?.companyName || "";
    const title = "Stock Return Report";

    const drawHeader = (pageNumber) => {
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.2);

      doc.setFont(undefined, "bold");
      doc.setFontSize(12);
      const companyNameWidth = doc.getTextWidth(companyName);
      const logoWidth = 5;
      const logoHeight = 10;
      const logoX = left + 6 + (companyNameWidth - logoWidth) / 2;
      const logoY = 8;

      doc.addImage(pdfLogo, "PNG", logoX, logoY, logoWidth, logoHeight);
      doc.text(companyName, left + 6, 23);

      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(`Report Date: ${utility.formatExportDateTime(new Date())}`, right, 23, { align: "right" });

      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.text(title, pageWidth / 2, 35, { align: "center" });

      const titleWidth = doc.getTextWidth(title);
      doc.setLineWidth(0.3);
      doc.line(pageWidth / 2 - titleWidth / 2, 37, pageWidth / 2 + titleWidth / 2, 37);
    };

    drawHeader(1);
    let yPosition = 45;

    doc.setFontSize(9);
    doc.setFont(undefined, "bold");

    let xLeft = 14;
    let xRight = 115;
    let yHeader = yPosition;

    doc.setFont(undefined, "bold");
    doc.text("From Date:", xLeft, yHeader);
    doc.setFont(undefined, "normal");
    doc.text(startDate ? utility.formatDateDDMMMYYYY(startDate) : "-", xLeft + 20, yHeader);

    doc.setFont(undefined, "bold");
    doc.text("To Date:", xLeft, yHeader + 5);
    doc.setFont(undefined, "normal");
    doc.text(endDate ? utility.formatDateDDMMMYYYY(endDate) : "-", xLeft + 20, yHeader + 5);

    doc.setFont(undefined, "bold");
    doc.text("Project:", xLeft, yHeader + 10);
    doc.setFont(undefined, "normal");
    doc.text(
      formatProjectDisplay({
        projectName: allData[0]?.projectDisplay || allData[0]?.projectName || "",
        projectCategoryName: allData[0]?.projectCategoryName || allData[0]?.categoryName || allData[0]?.category || ""
      }) || "All",
      xLeft + 20,
      yHeader + 10
    );

    yPosition += 20;

    const sortedData = [...allData].sort((a, b) => {
      const dateA = new Date(a.returnDate || 0).getTime();
      const dateB = new Date(b.returnDate || 0).getTime();
      return dateA - dateB;
    });

    const body = sortedData.map((row, index) => [
      index + 1,
      utility.formatDateDDMMMYYYY(row.returnDate),
      row.storeName || "-",
      row.locationName || "-",
      row.receivedBy || "-",
      row.transporterName || "-",
      formatProjectDisplay(row),
      row.vocherNo || "-",
      row.itemCategoryName || "-",
      row.itemCode || "-",
      row.itemDescription || "-",
      row.UOM || "-",
      row.quantity || "-",
      row.status || "-"
    ]);
    const totalQuantity = sortedData.reduce((sum, row) => {
      const quantity = Number(row.quantity);
      return Number.isFinite(quantity) ? sum + quantity : sum;
    }, 0);

    autoTable(doc, {
      startY: yPosition,

      head: [[
        "S.no",
        "Stock Return Date",
        "Store Name",
        "From Location",
        "Received By",
        "Transporter",
        "Project",
        "Voucher Number",
        "Item Category",
        "Item Code",
        "Item Description",
        "UOM",
        "Qty",
        "Status"
      ]],
      body,

      foot: [[
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "Total",
        totalQuantity.toFixed(3),
        ""
      ]],

      showFoot: "lastPage",

      theme: "grid",

      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.15,
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0]
      },

      alternateRowStyles: {
        fillColor: [255, 255, 255]
      },

      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold"
      },

      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold"
      },

      margin: {
        top: 40,
        left: 14,
        right: 14
      },

      didDrawPage: function (data) {
        if (data.pageNumber === 1) return;
        drawHeader(data.pageNumber);
        doc.setFontSize(9);
      }
    });

    const pageCount = doc.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      doc.setFontSize(9);
      doc.setFont(undefined, "normal");

      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" }
      );

      if (i === pageCount) {
        doc.setFontSize(9);
        doc.text("Prepared By", 20, pageHeight - 22);
        doc.text("Checked By", pageWidth / 2, pageHeight - 22, { align: "center" });
        doc.text("Approved By", pageWidth - 20, pageHeight - 22, { align: "right" });
      }
    }

    doc.save("StockReturnReport.pdf");
  };

  return (
    <>
      <Breadcrumb pageTitle="Stock Return Report" secondTitle="Reports" />

      <MessageDialog
        message={message}
        type={msgType}
        setMessage={setMessage}
        setMsgType={setMsgType}
      />

      <div className="card p-4 bg-white shadow-sm">
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <label className="form-label">Project Category <span className="text-danger">*</span></label>
            <select
              className="form-select"
              value={projectCategoryId}
              onChange={(e) => {
                setProjectCategoryId(e.target.value);
                setProjectId("");
                setStoreId("");
                setLocationId("");
                setStartDate("");
              }}
            >
              <option value="">Select Project Category</option>
              {projectCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.categoryName}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Project Name <span className="text-danger">*</span></label>

            <select
              className="form-select"
              value={projectId}
              onChange={(e) => {
                const selectedProjectId = e.target.value;

                setProjectId(selectedProjectId);
                setStoreId("");
                setLocationId("");

                loadStoresByProject(selectedProjectId);

                if (!selectedProjectId) {
                  setStartDate("");
                  return;
                }

                const selectedProject = projects.find(
                  (p) => String(p.id || p.projectId) === String(selectedProjectId)
                );

                if (selectedProject?.projectStartDate) {
                  const formattedDate = new Date(selectedProject.projectStartDate)
                    .toISOString()
                    .split("T")[0];

                  setStartDate(formattedDate);
                } else {
                  setStartDate("");
                }
              }}
              disabled={!projectCategoryId}
            >
              <option value="">Select Project Name</option>

              {filteredProjects.map((project) => (
                <option key={project.projectId || project.id} value={project.projectId || project.id}>
                  {formatProjectDisplay(project)}
                </option>
              ))}
            </select>

          </div>

          <div className="col-md-3">
            <label className="form-label">Store</label>

            <select
              className="form-select"
              value={storeId}
              onChange={(e) => {
                const selectedStoreId = e.target.value;

                setStoreId(selectedStoreId);
                setLocationId("");

                loadLocationsByProjectAndStore(projectId, selectedStoreId);
              }}
              disabled={!projectId}
            >
              <option value="">All</option>

              {stores.map((store) => (
                <option key={store.id} value={store.storeId || store.id}>
                  {store.storeName}
                </option>
              ))}
            </select>

          </div>

          <div className="col-md-3">
            <label className="form-label">From Location</label>

            <select
              className="form-select"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              disabled={!storeId}
            >
              <option value="">All</option>

              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.locationName}
                </option>
              ))}
            </select>

          </div>

          <div className="col-md-3">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="Approved">Approved</option>
              <option value="Pending">Pending</option>
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">From Date <span className="text-danger">*</span></label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">To Date <span className="text-danger">*</span></label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>


        <div className="mb-3 d-flex justify-content-end align-items-center">

          <div className="d-flex gap-2">

            <button className="btn btn-secondary" onClick={handleClearFilters}>
              <i className="ti ti-refresh me-1"></i>
              Clear
            </button>

            <button className="btn btn-primary" onClick={() => loadReport(1)} disabled={loading}>
              <i className="ti ti-search me-1"></i>
              {loading ? "Searching..." : "View Report"}
            </button>
          </div>
        </div>
        <hr />
        <div className="mb-3 d-flex justify-content-end align-items-center">
          <div className="d-flex gap-2">
            <button className="btn btn-success" onClick={exportPdf} disabled={loading}>
              <i className="ti ti-file-type-pdf me-1"></i>
              Export PDF
            </button>

            <button className="btn btn-success" onClick={exportExcel} disabled={loading}>
              <i className="ti ti-file-spreadsheet me-1"></i>
              Export Excel
            </button>
          </div>


        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-striped mb-0">
            <thead className="table-light">

              <tr>
                <th>S.no</th>
                <th>Stock Return Date</th>
                <th>Store Name</th>
                <th>From Location</th>
                <th>Received By</th>
                <th>Transporter</th>
                <th>Project</th>
                <th>Voucher Number</th>
                <th>Item Category</th>
                <th>Item Code</th>
                <th>Item Description</th>
                <th>UOM</th>
                <th>Qty</th>
                <th>Status</th>
              </tr>

            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="14" className="text-center">
                    <div className="spinner-border text-primary" />
                  </td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan="14" className="text-center">
                    No data found
                  </td>
                </tr>
              ) : (
                reports.map((row, index) => (
                  <tr key={row.id || index}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td>{utility.formatDateDDMMMYYYY(row.returnDate)}</td>
                    <td>{row.storeName || "-"}</td>
                    <td>{row.locationName || "-"}</td>
                    <td>{row.receivedBy || "-"}</td>
                    <td>{row.transporterName || "-"}</td>
                    <td>{formatProjectDisplay(row)}</td>
                    <td>{row.vocherNo || "-"}</td>
                    <td>{row.itemCategoryName || "-"}</td>
                    <td>{row.itemCode || "-"}</td>
                    <td>{row.itemDescription || "-"}</td>
                    <td>{row.UOM || "-"}</td>
                    <td>{row.quantity ?? "-"}</td>
                    <td>
                      <span
                        style={{
                          backgroundColor:
                            (row.status || "").trim().toUpperCase() === "APPROVED"
                              ? "#28a745"
                              : (row.status || "").trim().toUpperCase() === "PENDING"
                                ? "#7f9eedff"
                                : (row.status || "").trim().toUpperCase() === "CANCELLED"
                                  ? "#6c757d"
                                  : (row.status || "").trim().toUpperCase() === "REJECTED"
                                    ? "#dc3545"
                                    : "#17a2b8",
                          color:
                            (row.status || "").trim().toUpperCase() === "PENDING"
                              ? "#000"
                              : "#fff",
                          padding: "4px 10px",
                          borderRadius: "20px",
                          fontSize: "11px",
                          fontWeight: "600",
                          display: "inline-block",
                          minWidth: "75px",
                          textAlign: "center"
                        }}
                      >
                        {row.status || "-"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">

              {/* TOTAL RECORDS */}
              <div>
                Total Records: <strong>{totalRecords}</strong>
              </div>

              {/* PAGINATION BUTTONS */}
              <div className="d-flex gap-2">

                <button
                  className="btn btn-sm btn-secondary"
                  disabled={page === 1}
                  onClick={() => loadReport(page - 1)}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    className={`btn btn-sm ${page === i + 1 ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => loadReport(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  className="btn btn-sm btn-secondary"
                  disabled={page === totalPages}
                  onClick={() => loadReport(page + 1)}
                >
                  Next
                </button>

              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
